/**
 * engine/selectors.ts
 *
 * Pure derived-state computations over board arrays and puzzle definitions.
 * No browser, DOM, Pixi, or side-effect imports.
 */

import { GameSessionState } from "./state";
import { GameAction } from "../state/actions";
import { sameItems } from "../utils/arrays";
import { isArrowSlot } from "./note-arrows";
import { BoardClueState, LineClueState, LineRef, RenderDiff, VisualClueCompletion } from "./types";

/** The hint assists: line hints shown while on, and a requested (explicit) hint. */
export interface HintAssists {
  showLineHints: boolean;
  availableHints: ReadonlySet<LineRef>;
  explicitHint: LineRef | null;
}

/**
 * The lines a hint points at: the available line hints while line hints are
 * shown, and the explicit hint. Shared by the board's hint rails and the line
 * view's flags and next-hint button.
 */
export function hintedLines(assists: HintAssists): LineRef[] {
  const out = assists.showLineHints ? [...assists.availableHints] : [];
  if (assists.explicitHint) out.push(assists.explicitHint);
  return out;
}

/**
 * Return the unique row indices and column indices touched by a set of flat
 * cell indices.
 *
 * @example
 * // 5-column board, cells 0, 1, 6 → rows 0,1 cols 0,1
 * getAffectedLines([0, 1, 6], 5) // → { rows: [0, 1], columns: [0, 1] }
 */
export function getAffectedLines(
  indices: number[],
  columns: number
): { rows: number[]; columns: number[] } {
  const rowSet = new Set<number>();
  const colSet = new Set<number>();

  for (const idx of indices) {
    rowSet.add(Math.floor(idx / columns));
    colSet.add(idx % columns);
  }

  return {
    rows: [...rowSet].sort((a, b) => a - b),
    columns: [...colSet].sort((a, b) => a - b),
  };
}

/**
 * Extract all cell values for a given row from a flat board array.
 *
 * @param board  Flat row-major cell array.
 * @param row    Zero-based row index.
 * @param columns Total column count.
 */
export function getRow(board: readonly number[], row: number, columns: number): number[] {
  const start = row * columns;
  return board.slice(start, start + columns);
}

/**
 * Extract all cell values for a given column from a flat board array.
 *
 * @param board   Flat row-major cell array.
 * @param col     Zero-based column index.
 * @param rows    Total row count.
 * @param columns Total column count.
 */
export function getColumn(
  board: readonly number[],
  col: number,
  rows: number,
  columns: number
): number[] {
  const result: number[] = new Array(rows);
  for (let r = 0; r < rows; r++) {
    result[r] = board[r * columns + col];
  }
  return result;
}

/**
 * Return completed clue indices whose span contains the given cell.
 * Incomplete feasible ranges are ignored so hover only lights resolved groups.
 * When header marks are given, a clue must also be marked, so manual marking
 * does not reveal clues the player has not crossed off.
 */
export function getHighlightedClueIndices(
  clueState: Pick<BoardClueState, 'rows' | 'columns'>,
  row: number,
  col: number,
  marks?: VisualClueCompletion,
): { row: number[]; column: number[] } {
  return {
    row: findCompletedSpansAt(clueState.rows[row], col, marks?.rows[row]),
    column: findCompletedSpansAt(clueState.columns[col], row, marks?.columns[col]),
  };
}

function findCompletedSpansAt(
  line: LineClueState | undefined,
  position: number,
  lineMarks: readonly boolean[] | undefined,
): number[] {
  if (!line) return [];

  const result: number[] = [];
  for (let i = 0; i < line.clueSpans.length; i++) {
    if (!line.completion[i]) continue;
    if (lineMarks && !lineMarks[i]) continue;
    const span = line.clueSpans[i];
    if (span && position >= span.start && position < span.endExclusive) {
      result.push(i);
    }
  }
  return result;
}

/** Every clue in the line is satisfied (a line with no clues never counts). */
function isLineComplete(line: LineClueState | undefined): boolean {
  return Boolean(line && line.completion.length > 0 && line.completion.every(Boolean));
}

/** Lines among the changed ones that are complete now and weren't before. */
export function newlyCompletedLines(
  before: GameSessionState['clueState'],
  after: GameSessionState['clueState'],
  changedRows: readonly number[],
  changedColumns: readonly number[],
): LineRef[] {
  const lines: LineRef[] = [];
  for (const index of changedRows) {
    if (isLineComplete(after.rows[index]) && !isLineComplete(before.rows[index])) lines.push({ axis: 'row', index });
  }
  for (const index of changedColumns) {
    if (isLineComplete(after.columns[index]) && !isLineComplete(before.columns[index])) lines.push({ axis: 'column', index });
  }
  return lines;
}

/** The session has had its PUZZLE_SOLVED (set once; only a new session clears it). */
/** Whether hints may be asked for: always solo; in a room, unless the host turned them off. */
export function hintsAllowed(state: Pick<GameSessionState, 'multiplayer'>): boolean {
  return state.multiplayer?.rules.hints ?? true;
}

/** Whether Check puzzle may fix the board: always solo; in a room, unless the host turned it off. */
export function checksAllowed(state: Pick<GameSessionState, 'multiplayer'>): boolean {
  return state.multiplayer?.rules.checks ?? true;
}

export function isSessionSolved(state: GameSessionState): boolean {
  return state.timer.solvedAtMs !== null;
}

/** Current game-time elapsed in ms, safe to call at any time. */
export function getElapsedMs(state: GameSessionState): number {
  const { startedAt, accumulatedMs, paused } = state.timer;
  if (paused || startedAt === null) return accumulatedMs;
  return accumulatedMs + (Date.now() - startedAt);
}

export const EMPTY_DIFF: RenderDiff = {
  changedCells: [],
  changedClueRows: [],
  changedClueColumns: [],
  cursorChanged: false,
  selectedColorChanged: false,
  paletteChanged: false,
  fullBoard: false,
  puzzleSolved: false,
  remoteCursorsChanged: false,
  hintsChanged: false,
  changedNotes: [],
  arrowsChanged: false,
};

/**
 * Cells a board action actually changed. Reads only the cells the action
 * touched (the stroke, the remote patch, or the command undo/redo applied)
 * instead of scanning the whole board. Returns null for other actions.
 */
export function changedCellsForBoardAction(
  prevState: GameSessionState,
  nextState: GameSessionState,
  action: GameAction,
): number[] | null {
  let candidates: readonly number[];
  switch (action.type) {
    case 'CELL_STROKE_COMMITTED':
    case 'REMOTE_PATCH_APPLIED':
      candidates = action.indices;
      break;
    case 'UNDO':
    case 'REDO':
      candidates = appliedChanges(prevState, nextState).cells;
      break;
    default:
      return null;
  }
  // Nothing on the board changed (an undo/redo of notes, or of nothing).
  if (prevState.board === nextState.board) return [];
  const changed: number[] = [];
  const seen = new Set<number>();
  for (const index of candidates) {
    if (seen.has(index)) continue;
    seen.add(index);
    if (prevState.board[index] !== nextState.board[index]) changed.push(index);
  }
  return changed;
}

/**
 * The cells and the notes the commands an action applied touch (a stroke or a
 * note change appends one to the log; an undo or a redo appends one per move
 * it stepped through: the player's move and the auto-cross moves after it).
 */
function appliedChanges(prevState: GameSessionState, nextState: GameSessionState): { cells: number[]; notes: number[]; arrows: boolean } {
  const changes = { cells: [] as number[], notes: [] as number[], arrows: false };
  const cellCount = nextState.notes.length;
  for (let i = prevState.history.log.length; i < nextState.history.log.length; i++) {
    const command = nextState.history.log[i];
    if (command.target !== 'notes') {
      changes.cells.push(...command.indices);
      continue;
    }
    // Indices past the cells are note arrow slots (engine/note-arrows); arrows are redrawn whole.
    for (const index of command.indices) {
      if (isArrowSlot(index, cellCount)) changes.arrows = true;
      else changes.notes.push(index);
    }
  }
  return changes;
}

/** True when the clue analysis describes the current board (no result is still on its way). */
export function clueStateIsCurrent(state: Pick<GameSessionState, 'boardRevision' | 'clueState'>): boolean {
  return state.clueState.revision === state.boardRevision;
}

export function computeRenderDiff(
  prevState: GameSessionState,
  nextState: GameSessionState,
  lastAction: GameAction
): RenderDiff {
  // A note move (a note stroke, a clear, or undo/redo of one) lists exactly the cells whose notes changed.
  const { cells: appliedCells, notes: changedNotes, arrows: arrowsChanged } = appliedChanges(prevState, nextState);
  // A load or a new puzzle replaces the log (and redraws everything); its commands aren't moves made now.
  const replacesLog = lastAction.type === 'BOARD_LOADED' || lastAction.type === 'PUZZLE_REPLACED';
  if (!replacesLog && (changedNotes.length > 0 || arrowsChanged) && appliedCells.length === 0) {
    return { ...EMPTY_DIFF, changedNotes, arrowsChanged };
  }

  const puzzleSolved = !prevState.clueState.puzzleSolved && nextState.clueState.puzzleSolved;
  const hintsChanged = hasHintStateChanged(prevState, nextState);

  switch (lastAction.type) {
    case 'CELL_STROKE_COMMITTED':
    case 'UNDO':
    case 'REDO': {
      const changedCells = changedCellsForBoardAction(prevState, nextState, lastAction) ?? [];
      // An undo can step back a note move and the auto crosses made after it.
      return { ...EMPTY_DIFF, changedCells, changedNotes, arrowsChanged, puzzleSolved, hintsChanged };
    }

    case 'REMOTE_PATCH_APPLIED':
      return { ...EMPTY_DIFF, changedCells: lastAction.indices, puzzleSolved, hintsChanged };

    case 'BOARD_LOADED':
      return { ...EMPTY_DIFF, fullBoard: true, puzzleSolved, hintsChanged };

    case 'PUZZLE_REPLACED':
      return replacedPuzzleDiff(prevState, nextState);

    case 'CLUE_RESOLUTION_BOARD_RESULT': {
      // Header marks can change on lines the analysis did not (e.g. automatic
      // marks replacing restored ones), so include those lines too.
      const marks = markLineChanges(prevState, nextState);
      return {
        ...EMPTY_DIFF,
        changedClueRows: mergeLines(lastAction.changedRows, marks.rows),
        changedClueColumns: mergeLines(lastAction.changedColumns, marks.columns),
        puzzleSolved,
      };
    }

    case 'CURSOR_UPDATED':
    case 'CURSOR_CLEARED':
      return { ...EMPTY_DIFF, cursorChanged: true };

    case 'SELECTED_COLOR_CHANGED':
      return { ...EMPTY_DIFF, selectedColorChanged: true };

    case 'PLAYERS_UPDATED':
    case 'REMOTE_CURSOR_MOVED':
    case 'REMOTE_CURSOR_CLEARED':
      return { ...EMPTY_DIFF, remoteCursorsChanged: true };

    case 'PALETTE_CUSTOMIZED':
      return { ...EMPTY_DIFF, paletteChanged: true };

    case 'CLUE_MARK_TOGGLED':
    case 'CLUE_MARKING_CHANGED':
    case 'MULTIPLAYER_SESSION_STARTED': {
      const marks = markLineChanges(prevState, nextState);
      if (marks.rows.length === 0 && marks.columns.length === 0) return EMPTY_DIFF;
      return { ...EMPTY_DIFF, changedClueRows: marks.rows, changedClueColumns: marks.columns };
    }

    case 'LINE_HINTS_UPDATED':
    case 'EXPLICIT_HINT_UPDATED':
    case 'LINE_HINT_ASSISTANCE_CHANGED':
      return { ...EMPTY_DIFF, hintsChanged };

    default:
      return EMPTY_DIFF;
  }
}

/**
 * A replaced puzzle of the same size redraws only the cells that differ (and
 * every cell when the palette changed); a new size redraws everything.
 */
function replacedPuzzleDiff(prevState: GameSessionState, nextState: GameSessionState): RenderDiff {
  const before = prevState.puzzle;
  const after = nextState.puzzle;
  const cursorChanged = prevState.cursor?.row !== nextState.cursor?.row || prevState.cursor?.column !== nextState.cursor?.column;
  if (before.rows !== after.rows || before.columns !== after.columns) {
    return { ...EMPTY_DIFF, fullBoard: true, cursorChanged: true, hintsChanged: true };
  }
  const paletteChanged = !sameItems(before.colors, after.colors);
  const changedCells: number[] = [];
  if (!paletteChanged) {
    const a = prevState.board;
    const b = nextState.board;
    for (let index = 0; index < b.length; index++) if (a[index] !== b[index]) changedCells.push(index);
  }
  return { ...EMPTY_DIFF, changedCells, paletteChanged, cursorChanged };
}

function mergeLines(a: number[], b: number[]): number[] {
  return b.length === 0 ? a : [...new Set([...a, ...b])];
}

/** Rows and columns whose header marks changed between two states. */
function markLineChanges(
  prevState: GameSessionState,
  nextState: GameSessionState,
): { rows: number[]; columns: number[] } {
  return {
    rows: changedMarkLines(prevState.visualClueCompletion.rows, nextState.visualClueCompletion.rows),
    columns: changedMarkLines(prevState.visualClueCompletion.columns, nextState.visualClueCompletion.columns),
  };
}

/** Lines whose mark array was replaced. Marks are copy-on-write per line. */
function changedMarkLines(prev: readonly boolean[][], next: readonly boolean[][]): number[] {
  const changed: number[] = [];
  for (let i = 0; i < next.length; i++) {
    if (prev[i] !== next[i]) changed.push(i);
  }
  return changed;
}

function hasHintStateChanged(prevState: GameSessionState, nextState: GameSessionState): boolean {
  return prevState.assists.showLineHints !== nextState.assists.showLineHints
    || prevState.assists.availableHints !== nextState.assists.availableHints
    || prevState.assists.explicitHint !== nextState.assists.explicitHint;
}
